// /////////////////////////////////////////////////////////////
// [ファイル] AgentList.tsx
// [概要] エージェント設定ダイアログの中央ペイン (定義の一覧)。検索・絞り込み・新規作成、
//        行の有効スイッチ、行のドラッグ (左レールのグループへドロップで付け替え) を持つ。
//        行は名前 / 説明の 2 行固定で、チップ類は右側にまとめる (名前が省略されにくい並び)。
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Box, Chip, Fab, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useDraggable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import * as Icons from '../../constants/icons';
import LoadingView from '../LoadingView';
import { STANDARD_FAB_CTA_SX } from '../../constants/buttonStyles';
import type { AgentItem } from '../../hooks/useAssistantAgents';
import { EASE_OUT, ISSUE_STATUS, KIND_LABEL, type ListFilter } from './agentSettingsShared';

interface Props {
  title: string;
  hint: string;
  items: AgentItem[];
  totalCount: number; // 絞り込み前の件数 (0 件の文言の切り替えに使う)
  loading: boolean;
  selectedId: string | null;
  query: string;
  onQuery: (q: string) => void;
  filter: ListFilter;
  onFilter: (f: ListFilter) => void;
  showType: boolean; // グループ面では種別チップを出す
  showGroup: boolean; // 種別面では所属グループのチップを出す
  groupNameOf: (item: AgentItem) => string;
  audienceCountOf: (item: AgentItem) => number; // 本人以外で見える人数
  canEdit: (item: AgentItem) => boolean;
  busyId: string | null;
  newDraft: { name: string; type: AgentItem['type'] } | null; // 作成中の定義 (一覧の先頭に仮の行を出す)
  canCreate: boolean;
  onSelect: (item: AgentItem) => void;
  onToggleEnabled: (item: AgentItem) => void;
  onCreate: () => void;
}

const FILTERS: { key: ListFilter; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'mine', label: '自分だけ' },
  { key: 'shared', label: '共有' },
  { key: 'off', label: '無効' },
];

/** 1 定義の行。編集できる定義はドラッグで左レールのグループへ落として付け替えられる */
function AgentRow({
  item,
  selected,
  editable,
  busy,
  showType,
  showGroup,
  groupName,
  audienceCount,
  onSelect,
  onToggleEnabled,
}: {
  item: AgentItem;
  selected: boolean;
  editable: boolean;
  busy: boolean;
  showType: boolean;
  showGroup: boolean;
  groupName: string;
  audienceCount: number;
  onSelect: () => void;
  onToggleEnabled: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id, disabled: !editable });
  const off = item.enabled === false;
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        columnGap: 1.25,
        rowGap: 0.25,
        alignItems: 'center',
        px: 1.25,
        py: 1,
        borderRadius: 2.25,
        border: 1,
        borderColor: selected ? blueGrey[200] : 'transparent',
        bgcolor: selected ? blueGrey[50] : 'transparent',
        cursor: editable ? 'grab' : 'pointer',
        outline: 'none',
        opacity: isDragging ? 0.4 : off ? 0.55 : 1,
        transform: isDragging ? 'scale(.98)' : 'none',
        transition: 'background .15s, border-color .15s, opacity .25s, transform .25s',
        touchAction: 'none',
        '&:hover': { bgcolor: selected ? blueGrey[50] : 'rgba(0,0,0,.04)' },
        '&:focus-visible': { borderColor: 'primary.main' },
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{ gridColumn: 1, gridRow: 1, fontWeight: 600, fontSize: '0.82rem', minWidth: 0 }}
      >
        {item.name || <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>(名称未設定)</Box>}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ gridColumn: 1, gridRow: 2, minWidth: 0, minHeight: '1.2em' }}
      >
        {item.description || (item.body ? item.body.split('\n')[0] : '')}
      </Typography>
      <Box
        sx={{ gridColumn: 2, gridRow: '1 / span 2', display: 'flex', alignItems: 'center', gap: 0.75 }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {showType && (
          <Chip size="small" label={KIND_LABEL[item.type]} sx={{ fontSize: '0.65rem', height: 20 }} />
        )}
        {item.type === 'issue' && item.status && ISSUE_STATUS[item.status] && (
          <Chip
            size="small"
            variant={item.status === 'done' ? 'outlined' : 'filled'}
            color={ISSUE_STATUS[item.status].color}
            label={ISSUE_STATUS[item.status].label}
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
        {showGroup && item.group_id && (
          <Chip
            size="small"
            color="primary"
            icon={<Icons.Folder />}
            label={groupName}
            sx={{ fontSize: '0.65rem', height: 20, maxWidth: 140, '& .MuiChip-icon': { fontSize: 12 } }}
          />
        )}
        {audienceCount > 0 && (
          <Tooltip title={`本人のほか ${audienceCount} 人に見えます`}>
            <Chip
              size="small"
              icon={<Icons.Groups />}
              label={audienceCount}
              sx={{ fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { fontSize: 12 } }}
            />
          </Tooltip>
        )}
        {busy ? (
          <Box sx={{ width: 38 }}>
            <LoadingView message="" height="20px" size={16} />
          </Box>
        ) : (
          <Tooltip title={editable ? (off ? 'AI へ渡していません (クリックで有効化)' : 'AI へ渡しています (クリックで無効化)') : '編集権限がありません'}>
            <span>
              <Switch
                size="small"
                checked={!off}
                disabled={!editable}
                onChange={onToggleEnabled}
                slotProps={{ input: { 'aria-label': 'AI へ渡す' } }}
              />
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default function AgentList({
  title,
  hint,
  items,
  totalCount,
  loading,
  selectedId,
  query,
  onQuery,
  filter,
  onFilter,
  showType,
  showGroup,
  groupNameOf,
  audienceCountOf,
  canEdit,
  busyId,
  newDraft,
  canCreate,
  onSelect,
  onToggleEnabled,
  onCreate,
}: Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%', borderRight: 1, borderColor: 'divider' }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, display: 'flex', flexDirection: 'column', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: blueGrey[600], lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {hint}
            </Typography>
          </Box>
          {canCreate && (
            <Fab variant="extended" size="small" onClick={onCreate} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Add />
              新規
            </Fab>
          )}
        </Box>
        <TextField
          size="small"
          placeholder="名前・説明・本文で検索"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icons.Search sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <Icons.Clear sx={{ fontSize: 14, cursor: 'pointer', color: 'text.secondary' }} onClick={() => onQuery('')} />
              </InputAdornment>
            ) : undefined,
            sx: { fontSize: '0.8rem', bgcolor: '#fff', height: 32 },
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Chip
              key={f.key}
              size="small"
              label={f.label}
              clickable
              onClick={() => onFilter(f.key)}
              sx={{
                fontSize: '0.68rem',
                height: 22,
                ...(filter === f.key
                  ? { bgcolor: blueGrey[600], color: '#fff', fontWeight: 600, '&:hover': { bgcolor: blueGrey[700] } }
                  : { bgcolor: 'transparent', border: 1, borderColor: 'divider', color: 'text.secondary' }),
              }}
            />
          ))}
        </Box>
      </Box>

      <Box role="listbox" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pt: 1, pb: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {loading && items.length === 0 ? (
          <LoadingView message="定義を読み込んでいます..." height="40vh" size={40} />
        ) : (
          <>
            {newDraft && (
              <Box
                role="option"
                aria-selected
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 2.25,
                  border: 1,
                  borderColor: 'primary.main',
                  borderStyle: 'dashed',
                  bgcolor: '#e3f2fd',
                }}
              >
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                  {newDraft.name || <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>(新しい{KIND_LABEL[newDraft.type]})</Box>}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  右で入力して保存すると一覧に加わります
                </Typography>
              </Box>
            )}
            {items.length === 0 && !newDraft ? (
              <Box sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Icons.Search sx={{ fontSize: 36, color: 'divider' }} />
                <Typography variant="caption">
                  {totalCount === 0 ? 'まだ登録がありません' : '条件に合う定義がありません'}
                </Typography>
                {canCreate && totalCount === 0 && (
                  <Fab variant="extended" size="small" onClick={onCreate} sx={STANDARD_FAB_CTA_SX}>
                    <Icons.Add />
                    最初の 1 件を作る
                  </Fab>
                )}
              </Box>
            ) : (
              items.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: EASE_OUT, delay: Math.min(i, 12) * 0.02 }}
                >
                  <AgentRow
                    item={a}
                    selected={selectedId === a.id}
                    editable={canEdit(a)}
                    busy={busyId === a.id}
                    showType={showType}
                    showGroup={showGroup}
                    groupName={groupNameOf(a)}
                    audienceCount={audienceCountOf(a)}
                    onSelect={() => onSelect(a)}
                    onToggleEnabled={() => onToggleEnabled(a)}
                  />
                </motion.div>
              ))
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
